/**
 * Product Image Zoom
 * - Desktop: hover-to-zoom functionality
 * - Mobile: fullscreen modal with drag/pan
 */

class ProductImageZoom {
  constructor() {
    this.isDesktop = window.innerWidth >= 769;
    this.zoomLevel = 2;

    // Mobile zoom modal properties
    this.modal = null;
    this.modalImage = null;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;

    this.init();
    this.setupResizeListener();
  }

  init() {
    // Wait for swiper to initialize
    const checkSwiper = setInterval(() => {
      const productSlider = document.querySelector('.js-product__media-list--slider');

      if (productSlider && productSlider.classList.contains('swiper-initialized')) {
        clearInterval(checkSwiper);

        if (this.isDesktop) {
          this.setupDesktopZoom();
        } else {
          this.setupMobileZoom();
        }
      }
    }, 100);
  }

  setupDesktopZoom() {
    const mediaItems = document.querySelectorAll('.product__media-item .product__media');

    mediaItems.forEach(mediaElement => {
      // Only enable zoom for images (not videos)
      const isImage = mediaElement.querySelector('.img-fill') && !mediaElement.querySelector('video');

      if (isImage) {
        mediaElement.classList.add('zoom-enabled');

        // Mouse enter - start zoom
        mediaElement.addEventListener('mouseenter', () => {
          mediaElement.classList.add('zooming');
        });

        // Mouse move - update zoom position
        mediaElement.addEventListener('mousemove', (e) => {
          this.updateZoomPosition(mediaElement, e);
        });

        // Mouse leave - end zoom
        mediaElement.addEventListener('mouseleave', () => {
          mediaElement.classList.remove('zooming');
          this.resetZoomPosition(mediaElement);
        });
      }
    });
  }

  setupMobileZoom() {
    // Get modal elements
    this.modal = document.getElementById('productZoomModal');
    this.modalImage = this.modal?.querySelector('.product-zoom-modal__image');
    const closeButton = this.modal?.querySelector('.product-zoom-modal__close');

    if (!this.modal || !this.modalImage) return;

    // Setup single zoom trigger button
    const zoomTrigger = document.querySelector('.product__media-zoom-trigger');

    if (zoomTrigger) {
      zoomTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Get active slide from swiper
        const productSlider = document.querySelector('.js-product__media-list--slider');
        if (productSlider && productSlider.swiper) {
          const activeIndex = productSlider.swiper.activeIndex;
          const activeSlide = productSlider.querySelector(`.swiper-slide:nth-child(${activeIndex + 1})`);

          if (activeSlide) {
            const productMedia = activeSlide.querySelector('.product__media');
            const imageSrc = productMedia?.dataset.imageSrc;

            if (imageSrc) {
              this.openModal(imageSrc);
            }
          }
        }
      });
    }

    // Setup close button
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeModal();
      });
    }

    // Setup drag functionality
    this.setupDragFunctionality();
  }

  openModal(imageSrc) {
    if (!this.modal || !this.modalImage) return;

    const loader = this.modal.querySelector('.product-zoom-modal__loader');

    // Show loader
    if (loader) {
      loader.classList.remove('hidden');
    }

    // Hide previous image
    this.modalImage.classList.remove('loaded');
    this.modalImage.src = '';

    // Reset position
    this.currentX = 0;
    this.currentY = 0;
    this.updateImagePosition();

    // Show modal
    this.modal.classList.add('active');

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Load new image
    const img = new Image();
    img.onload = () => {
      this.modalImage.src = imageSrc;
      this.modalImage.classList.add('loaded');

      // Hide loader
      if (loader) {
        setTimeout(() => {
          loader.classList.add('hidden');
        }, 100);
      }
    };
    img.src = imageSrc;
  }

  closeModal() {
    if (!this.modal) return;

    this.modal.classList.remove('active');

    // Restore body scroll
    document.body.style.overflow = '';
  }

  setupDragFunctionality() {
    if (!this.modalImage) return;

    // Touch events
    this.modalImage.addEventListener('touchstart', (e) => {
      this.handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
      e.preventDefault();
    });

    this.modalImage.addEventListener('touchmove', (e) => {
      if (this.isDragging) {
        this.handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
        e.preventDefault();
      }
    });

    this.modalImage.addEventListener('touchend', () => {
      this.handleDragEnd();
    });

    // Mouse events (for testing on desktop)
    this.modalImage.addEventListener('mousedown', (e) => {
      this.handleDragStart(e.clientX, e.clientY);
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (this.isDragging) {
        this.handleDragMove(e.clientX, e.clientY);
      }
    });

    document.addEventListener('mouseup', () => {
      this.handleDragEnd();
    });
  }

  handleDragStart(clientX, clientY) {
    this.isDragging = true;
    this.startX = clientX - this.currentX;
    this.startY = clientY - this.currentY;

    if (this.modalImage) {
      this.modalImage.classList.add('dragging');
    }
  }

  handleDragMove(clientX, clientY) {
    if (!this.isDragging || !this.modalImage) return;

    // Calculate new position
    let newX = clientX - this.startX;
    let newY = clientY - this.startY;

    // Get container dimensions
    const container = this.modal.querySelector('.product-zoom-modal__container');
    if (!container) return;

    const containerRect = container.getBoundingClientRect();

    // Image is rendered at 200% of container (min-width: 200%, min-height: 200% in CSS)
    const zoomFactor = 2;
    const renderedWidth = containerRect.width * zoomFactor;
    const renderedHeight = containerRect.height * zoomFactor;

    // Calculate boundaries (allow 20% of image to go outside)
    const allowedOffset = 0.2; // 20%
    const maxX = (renderedWidth - containerRect.width) / 2 + (renderedWidth * allowedOffset);
    const maxY = (renderedHeight - containerRect.height) / 2 + (renderedHeight * allowedOffset);

    // Apply boundary limits
    newX = Math.max(-maxX, Math.min(maxX, newX));
    newY = Math.max(-maxY, Math.min(maxY, newY));

    this.currentX = newX;
    this.currentY = newY;

    this.updateImagePosition();
  }

  handleDragEnd() {
    this.isDragging = false;

    if (this.modalImage) {
      this.modalImage.classList.remove('dragging');
    }
  }

  updateImagePosition() {
    if (!this.modalImage) return;

    this.modalImage.style.transform = `translate(${this.currentX}px, ${this.currentY}px)`;
  }

  updateZoomPosition(mediaElement, event) {
    const image = mediaElement.querySelector('.img-fill');
    if (!image) return;

    const rect = mediaElement.getBoundingClientRect();

    // Calculate mouse position relative to the element (0 to 1)
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    // Calculate transform origin (as percentage)
    const xPercent = x * 100;
    const yPercent = y * 100;

    // Update transform origin to follow mouse
    image.style.transformOrigin = `${xPercent}% ${yPercent}%`;
  }

  resetZoomPosition(mediaElement) {
    const image = mediaElement.querySelector('.img-fill');
    if (!image) return;

    // Reset to center
    image.style.transformOrigin = 'center center';
  }

  setupResizeListener() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.innerWidth >= 769;

        // If switching between mobile/desktop, reinitialize or cleanup
        if (wasDesktop !== this.isDesktop) {
          if (this.isDesktop) {
            this.init();
          } else {
            this.cleanup();
          }
        }
      }, 250);
    });
  }

  cleanup() {
    const mediaItems = document.querySelectorAll('.product__media-item .product__media');
    mediaItems.forEach(mediaElement => {
      mediaElement.classList.remove('zoom-enabled', 'zooming');
      const image = mediaElement.querySelector('.img-fill');
      if (image) {
        image.style.transformOrigin = '';
      }
    });
  }

  // Public method to reinitialize zoom (useful after variant changes)
  reinit() {
    if (this.isDesktop) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        this.setupDesktopZoom();
      }, 100);
    } else {
      // For mobile, just re-setup the zoom button listener
      setTimeout(() => {
        this.setupMobileZoom();
      }, 100);
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productImageZoom = new ProductImageZoom();
  });
} else {
  window.productImageZoom = new ProductImageZoom();
}